import { h } from 'preact';

import { SCENE_CSS } from './keyframes';
import {
    layerTransform, DEPTH_SKY, DEPTH_FAR, DEPTH_MID, DEPTH_GROUND, DEPTH_HERO,
} from './parallax';
import { Cloud, GroundBand } from './sceneProps';

// Distant bird — two wing arcs.
const Bird = ({ x, y, s = 1, stroke }) => (
    <path
        d={`M${x} ${y} q ${5 * s} ${-6 * s} ${10 * s} 0 q ${5 * s} ${-6 * s} ${10 * s} 0`}
        fill='none'
        stroke={stroke}
        stroke-width='1.5'
        opacity='0.55'
    />
);

// City skyline: [x, roofY, width] — buildings overdraw down past the ground line.
const BUILDINGS = [
    [-40, 88, 46], [10, 70, 34], [50, 94, 52], [108, 78, 30], [144, 60, 26],
    [176, 86, 44], [226, 72, 34], [266, 92, 50], [322, 64, 28], [356, 82, 40],
    [402, 94, 46], [454, 70, 32], [492, 86, 46], [544, 58, 28], [578, 80, 42],
    [626, 92, 48], [680, 68, 30], [716, 84, 44], [766, 74, 34],
];

const PaperPlane = ({
    progress = 0, pastThreshold = false, released = false,
    pullOffset = 0, palette = {},
}) => {
    const driftSpeed = (28 - 14 * progress).toFixed(2);
    const planeAnimation = released
        ? 'animation: refreshIconSwoop 0.7s ease-out forwards;'
        : `animation: refreshWobble ${(3 - 1.5 * progress).toFixed(2)}s ease-in-out infinite;`;

    return (
        <div style={`position: relative; width: 100%; height: 100%; ${pastThreshold ? 'filter: brightness(1.1);' : ''}`}>
            <svg
                viewBox='0 0 800 150'
                preserveAspectRatio='xMidYMax slice'
                role='img'
                class='refresh-scene'
                style='position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;'
            >
                <style>{SCENE_CSS}</style>
                <title>Dra ned for å oppdatere</title>

                {/* Sky */}
                <g style={layerTransform(pullOffset, DEPTH_SKY)}>
                    <Cloud x={90} y={22} s={1.2} fill={palette.sky} />
                    <Cloud x={340} y={14} s={0.9} fill={palette.sky} />
                    <Cloud x={560} y={26} s={1.3} fill={palette.sky} />
                    <Cloud x={740} y={16} s={0.8} fill={palette.sky} />
                </g>

                {/* Far — birds */}
                <g style={layerTransform(pullOffset, DEPTH_FAR)}>
                    <Bird x={180} y={42} stroke={palette.text} />
                    <Bird x={250} y={34} s={0.7} stroke={palette.text} />
                    <Bird x={540} y={38} s={0.8} stroke={palette.text} />
                </g>

                {/* Mid — city skyline, drifting by */}
                <g style={layerTransform(pullOffset, DEPTH_MID)}>
                    <g class='drift' style={`animation-duration: ${driftSpeed}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {BUILDINGS.map(([x, roofY, width]) => (
                                    <rect key={x} x={x} y={roofY} width={width} height={160 - roofY} fill={palette.mid} />
                                ))}
                            </g>
                        ))}
                    </g>
                </g>

                {/* Ground */}
                <g style={layerTransform(pullOffset, DEPTH_GROUND)}>
                    <GroundBand y={132} fill={palette.ground} />
                </g>

                {/* Release — speed lines behind the plane */}
                {released && (
                    <g stroke={palette.accent} stroke-width='3' stroke-linecap='round'>
                        {[64, 74, 84].map((y, idx) => (
                            <line
                                key={y}
                                x1='300'
                                y1={y}
                                x2='352'
                                y2={y}
                                style={`animation: refreshSpeedLine 0.7s ease-out ${(idx * 0.06).toFixed(2)}s forwards; opacity: 0;`}
                            />
                        ))}
                    </g>
                )}
            </svg>

            {/* Hero — paper plane gliding above the skyline */}
            <div style={`position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; pointer-events: none; ${layerTransform(pullOffset, DEPTH_HERO)}`}>
                <i
                    class='fa-duotone fa-paper-plane'
                    style={`font-size: 60px; color: ${palette.text}; --fa-secondary-opacity: 0.45; padding-bottom: 56px; ${planeAnimation}`}
                />
            </div>
        </div>
    );
};

export default PaperPlane;
